import {
  signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signOut, onIdTokenChanged, updateProfile, deleteUser, sendEmailVerification,
  reload,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase.js';
import { initializeAccount } from './wallet.js';
import { registerNewLoginSession } from './session.js';

// ── Role flags (school owner / site administrator) ──────────────────────
// Reported by the server on every initializeAccount call and cached
// per-uid (so two accounts sharing a browser never see each other's tabs).
// These drive the "School" and "Admin" topbar buttons — no one should ever
// need to type #/school or #/dwar by hand. Purely cosmetic: both routes
// stay server-guarded regardless.
const SCHOOL_OWNER_KEY_PREFIX = 'll-school-owner:';
const ADMIN_KEY_PREFIX = 'll-admin:';
const TEACHER_KEY_PREFIX = 'll-teacher:';
const CAN_GENERATE_KEY_PREFIX = 'll-can-generate:';

// This is an authorized Firebase Auth domain in the production project (and
// localhost is authorized for development). Firebase handles the one-time
// verification action, then returns the person to the screen that can refresh
// their sign-in state and unlock the app.
const EMAIL_VERIFICATION_ACTION_SETTINGS = {
  url: `${window.location.origin}/#/verify-email`,
  handleCodeInApp: false,
};

function readFlag(prefix) {
  const uid = auth.currentUser?.uid;
  return !!uid && localStorage.getItem(prefix + uid) === '1';
}

function writeFlag(prefix, uid, value) {
  if (!uid) return;
  const key = prefix + uid;
  const next = value ? '1' : '0';
  const changed = localStorage.getItem(key) !== next;
  localStorage.setItem(key, next);
  if (changed) {
    // Re-resolve the current route so an already-rendered topbar picks the
    // tab up immediately instead of waiting for the next navigation.
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }
}

export function isSchoolOwnerCached() {
  return readFlag(SCHOOL_OWNER_KEY_PREFIX);
}

export function setSchoolOwnerCached(uid, isOwner) {
  writeFlag(SCHOOL_OWNER_KEY_PREFIX, uid, isOwner);
}

export function isAdminCached() {
  return readFlag(ADMIN_KEY_PREFIX);
}

export function setAdminCached(uid, isAdmin) {
  writeFlag(ADMIN_KEY_PREFIX, uid, isAdmin);
}

export function isTeacherCached() {
  return readFlag(TEACHER_KEY_PREFIX);
}

export function setTeacherCached(uid, isTeacher) {
  writeFlag(TEACHER_KEY_PREFIX, uid, isTeacher);
}

// canGenerateCached — mirrors functions/wallet.js initializeAccount's
// canGenerate flag (the appConfig/access.generationRestrictedToStaff
// pilot gate). UX only, same as the role flags above — the server-side
// checkEntitlement check is the real enforcement. Defaults to true
// (unrestricted) before the first initializeAccount response of a session
// ever lands, via readFlagDefaultTrue below, so a fresh sign-in never
// flashes a false "you can't generate" before the real answer arrives.
function readFlagDefaultTrue(prefix) {
  const uid = auth.currentUser?.uid;
  if (!uid) return true;
  const stored = localStorage.getItem(prefix + uid);
  return stored === null ? true : stored === '1';
}

export function canGenerateCached() {
  return readFlagDefaultTrue(CAN_GENERATE_KEY_PREFIX);
}

export function setCanGenerateCached(uid, canGenerate) {
  writeFlag(CAN_GENERATE_KEY_PREFIX, uid, canGenerate);
}

// Grants the one-time free 30-day viewing period (see functions/wallet.js
// initializeAccount) — safe to call on every sign-in since it's a no-op
// after the first call for a given account. Never allowed to block or
// fail a sign-in: a transient network/Functions error here shouldn't lock
// someone out of an app they just successfully authenticated into: the
// same call fires again on their next login attempt. Also refreshes the
// per-uid school-owner flag from the server's response.
async function ensureAccountInitialized() {
  try {
    const result = await initializeAccount();
    const uid = auth.currentUser?.uid;
    if (uid && typeof result?.isSchoolOwner === 'boolean') {
      setSchoolOwnerCached(uid, result.isSchoolOwner);
    }
    if (uid && typeof result?.isAdmin === 'boolean') {
      setAdminCached(uid, result.isAdmin);
    }
    if (uid && typeof result?.isTeacher === 'boolean') {
      setTeacherCached(uid, result.isTeacher);
    }
    if (uid && typeof result?.canGenerate === 'boolean') {
      setCanGenerateCached(uid, result.canGenerate);
    }
    // A school invoice was just issued for this sign-in (first login of the
    // seat term for a rostered student — see functions/schools.js
    // maybeIssueStudentInvoice). Announce it once; the invoice itself lives
    // on the Wallet page ("School invoices").
    if (result?.newInvoice) {
      const inv = result.newInvoice;
      const el = document.createElement('div');
      el.className = 'toast';
      el.textContent = `🧾 ${inv.schoolName || 'Your school'} has issued your invoice — view it on your Wallet page.`;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 8000);
    }
  } catch (err) {
    console.error('initializeAccount failed', err);
  }
}

// For persistent sessions (page reload with an already-signed-in user, where
// none of the explicit sign-in functions below run): refresh account flags
// in the background. Fire-and-forget by design.
export function refreshAccountFlags() {
  ensureAccountInitialized();
}

export function watchAuth(callback) {
  // Email verification changes a user's ID token, not their sign-in state.
  // Listening for token changes makes the app notice immediately after the
  // verification page refreshes the current user.
  return onIdTokenChanged(auth, callback);
}

export function currentUser() {
  return auth.currentUser;
}

// Only email/password accounts need this extra confirmation. OAuth providers
// (for example Google) have already verified ownership of their email address.
export function requiresEmailVerification(user = auth.currentUser) {
  return Boolean(
    user?.email
    && user.providerData.some((provider) => provider.providerId === 'password')
    && !user.emailVerified,
  );
}

export async function resendEmailVerification() {
  const user = auth.currentUser;
  if (!user || !requiresEmailVerification(user)) {
    throw new Error('No unverified email account is signed in.');
  }
  // Firebase Authentication sends this from its configured verification-email
  // template and hosts the one-time action link securely.
  await sendEmailVerification(user, EMAIL_VERIFICATION_ACTION_SETTINGS);
}

export async function refreshEmailVerification() {
  const user = auth.currentUser;
  if (!user) return false;
  await reload(user);
  return !requiresEmailVerification(auth.currentUser);
}

async function ensureProfile(user) {
  const ref = doc(db, 'users', user.uid, 'profile', 'main');
  await setDoc(ref, {
    displayName: user.displayName || user.email || 'Learner',
    email: user.email || null,
    createdAt: serverTimestamp(),
  }, { merge: true });
}

// Each of these three is an EXPLICIT sign-in action — the only place a new
// single-session id should be minted (see lib/session.js's header comment
// for why a page reload must never do this).
//
// registerNewLoginSession must come FIRST, before ensureProfile /
// ensureAccountInitialized. Firebase fires onAuthStateChanged the moment the
// credential resolves, and main.js starts the session guard from there — so
// any await placed ahead of the claim is a window in which the guard
// evaluates ownership for a login that hasn't claimed the slot yet. With two
// network round-trips in that window, the guard reliably won: it compared
// this browser's stale id against the server's and signed the user out
// before their own login could ever register, which is unrecoverable because
// every retry loses the same race. See lib/session.js for the matching
// in-flight-claim handling on the guard side.
export async function signInWithGoogle() {
  const cred = await signInWithPopup(auth, googleProvider);
  await registerNewLoginSession(cred.user.uid);
  await ensureProfile(cred.user);
  await ensureAccountInitialized();
  return cred.user;
}

export async function signUpWithEmail(email, password, displayName) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) await updateProfile(cred.user, { displayName });
  // Do not create an app session or initialize billable account state until
  // this address has been proven to belong to the person who registered.
  await sendEmailVerification(cred.user, EMAIL_VERIFICATION_ACTION_SETTINGS);
  return cred.user;
}

export async function signInWithEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  // A browser can hold a stale user object after the person used the email
  // link in another tab/device, so refresh it before deciding whether access
  // can be granted.
  await reload(cred.user);
  if (requiresEmailVerification(auth.currentUser)) return auth.currentUser;
  await registerNewLoginSession(cred.user.uid);
  await ensureProfile(cred.user);
  await ensureAccountInitialized();
  return cred.user;
}

export async function signOutUser() {
  await signOut(auth);
}

// Deletes the signed-in user's Auth account. Caller is responsible for
// wiping Firestore/Storage data first (see lib/deleteAccount.js) since
// Auth deletion can't cascade to those.
export async function deleteCurrentAuthUser() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  await deleteUser(user);
}
